/**
 * Ojar Multi-Currency System (Refactored v2.1)
 * Enhanced with client-side price application from OjarPriceData
 *
 * Handles:
 * 1. Geo-detection
 * 2. Currency selection & switching
 * 3. Cookie/localStorage management for Liquid templates
 * 4. Client-side price formatting from OjarPriceData global object
 *
 * Price rendering approach:
 * - Server renders Shopify prices as fallback
 * - Client applies custom prices from window.OjarPriceData if available
 * - No MutationObservers or DOM polling - only event-based updates
 */

(function () {
  "use strict";

  // Configuration
  const CONFIG = {
    storageKey: "ojar_currency",
    countryKey: "ojar_country",
    cookieName: "ojar_currency",
    cookieCountryName: "ojar_country",
    cookieDays: 365,
    geoApiUrl: "https://ipapi.co/json/",
    defaultCurrency: "USD",
    defaultCountry: "US",
  };

  // Supported currencies
  const SUPPORTED_CURRENCIES = [
    "USD",
    "EUR",
    "GBP",
    "SAR",
    "AED",
    "QAR",
    "OMR",
    "KWD",
    "BHD",
  ];

  // Currency formatting configuration (decimals and symbols)
  const CURRENCY_SYMBOLS = {
    USD: { symbol: "$", decimals: 2 },
    EUR: { symbol: "€", decimals: 2 },
    GBP: { symbol: "£", decimals: 2 },
    SAR: { symbol: "SAR ", decimals: 0 },
    AED: { symbol: "AED ", decimals: 0 },
    QAR: { symbol: "QAR ", decimals: 0 },
    OMR: { symbol: "OMR ", decimals: 3 },
    KWD: { symbol: "KWD ", decimals: 3 },
    BHD: { symbol: "BHD ", decimals: 3 },
  };

  // Country to Currency mapping
  const COUNTRY_CURRENCY_MAP = {
    // GCC
    AE: "AED",
    SA: "SAR",
    KW: "KWD",
    BH: "BHD",
    OM: "OMR",
    QA: "QAR",
    // Europe
    GB: "GBP",
    DE: "EUR",
    FR: "EUR",
    IT: "EUR",
    ES: "EUR",
    NL: "EUR",
    BE: "EUR",
    AT: "EUR",
    IE: "EUR",
    PT: "EUR",
    GR: "EUR",
    FI: "EUR",
    LU: "EUR",
    MT: "EUR",
    CY: "EUR",
    SK: "EUR",
    SI: "EUR",
    EE: "EUR",
    LV: "EUR",
    LT: "EUR",
    // North America
    US: "USD",
    CA: "USD",
  };

  // State
  let currentCurrency = CONFIG.defaultCurrency;
  let currentCountry = CONFIG.defaultCountry;

  // Debounce mechanism to prevent excessive applyMultiCurrencyPrices calls
  let priceApplyTimeout = null;
  let lastPriceApplyTime = 0;
  const MIN_PRICE_APPLY_INTERVAL = 50; // Minimum ms between price application calls

  /**
   * Cookie helper functions
   */
  function setCookie(name, value, days) {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  }

  function getCookie(name) {
    const nameEQ = name + "=";
    const cookies = document.cookie.split(";");
    for (let i = 0; i < cookies.length; i++) {
      let c = cookies[i].trim();
      if (c.indexOf(nameEQ) === 0) {
        return c.substring(nameEQ.length);
      }
    }
    return null;
  }

  /**
   * Format price according to currency decimal and symbol configuration
   * @param {number} amount - Price amount
   * @param {string} currencyCode - Currency code (USD, EUR, etc.)
   * @returns {string} Formatted price string
   */
  function formatPrice(amount, currencyCode) {
    const config = CURRENCY_SYMBOLS[currencyCode] || CURRENCY_SYMBOLS["USD"];
    let formatted;

    if (config.decimals === 0) {
      formatted = Math.round(amount).toString();
    } else {
      formatted = parseFloat(amount).toFixed(config.decimals);
    }

    return config.symbol + formatted;
  }

  /**
   * Calculate conversion rate from USD to target currency using product prices
   * @param {string} targetCurrency - Target currency code
   * @returns {number} Conversion rate (e.g., 0.85 for GBP)
   */
  function getConversionRate(targetCurrency) {
    if (targetCurrency === "USD") return 1;

    if (!window.OjarPriceData) return 1;

    // Find first product that has prices in both USD and target currency
    for (const productHandle in window.OjarPriceData) {
      if (productHandle === "_conversion_rates") continue;

      const priceData = window.OjarPriceData[productHandle];
      if (priceData && priceData.USD && priceData[targetCurrency]) {
        const rate = priceData[targetCurrency] / priceData.USD;
        // console.log(
        //   "[OjarCurrency] Conversion rate USD->",
        //   targetCurrency,
        //   ":",
        //   rate
        // );
        return rate;
      }
    }

    // console.warn(
    //   "[OjarCurrency] Could not calculate conversion rate for",
    //   targetCurrency
    // );
    return 1; // Fallback: no conversion
  }

  /**
   * Calculate cart total from line items with custom prices
   * @returns {number|null} Total in cents or null if cart is not available
   */
  function calculateCartTotalFromLineItems() {
    // Get all cart items with product handles (table rows only, not price elements)
    const cartItems = document.querySelectorAll("tr[data-product-handle]");

    let cartTotal = 0;
    let itemsProcessed = 0;

    // console.log(
    //   "[OjarCurrency] Calculating cart total from",
    //   cartItems.length,
    //   "items"
    // );
    // console.log(
    //   "[OjarCurrency] OjarPriceData available:",
    //   !!window.OjarPriceData
    // );

    cartItems.forEach((item) => {
      const handle = item.dataset.productHandle;
      let isSample =
        item.dataset?.tags?.includes("sample-product") ||
        item.dataset?.isSample === "true" ||
        item.classList?.contains("cart-item__sample");
      // Try to find quantity input - it could be nested in various ways
      // On cart page it's in a <tr>, on cart drawer it might be elsewhere
      let quantityInput =
        item.querySelector("[data-quantity-variant-id]") ||
        item.querySelector("input[type='number'][readonly]") ||
        item.querySelector("input[type='number']");

      if (
        !handle ||
        !quantityInput ||
        !window.OjarPriceData ||
        !window.OjarPriceData[handle]
      ) {
        if (!handle) {
          // console.warn("[OjarCurrency] No handle found");
        }
        if (!quantityInput) {
          // console.warn("[OjarCurrency] No quantity input found for:", handle);
        }
        if (!window.OjarPriceData || !window.OjarPriceData[handle]) {
          // console.warn("[OjarCurrency] No price data for:", handle);
        }
        return;
      }

      const quantity = parseInt(quantityInput.value) || 0;
      if (quantity <= 0) {
        // console.log("[OjarCurrency] Skipping item with 0 quantity:", handle);
        return;
      }

      const priceInCurrency = window.OjarPriceData[handle][currentCurrency];
      if (priceInCurrency === undefined) {
        // console.warn(
        //   "[OjarCurrency] No price for",
        //   currentCurrency,
        //   "in:",
        //   handle
        // );
        return;
      }

      // OjarPriceData prices are already in decimal format (e.g., 365 = $365.00)
      // Multiply by quantity to get line total
      const lineTotal = priceInCurrency * quantity;
      cartTotal += isSample ? 0 : lineTotal;
      itemsProcessed++;

      // console.log(
      //   "[OjarCurrency] Item",
      //   handle,
      //   "qty:",
      //   quantity,
      //   "price:",
      //   priceInCurrency,
      //   "lineTotal:",
      //   lineTotal
      // );
    });

    // console.log(
    //   "[OjarCurrency] Calculated cart total:",
    //   cartTotal,
    //   "from",
    //   itemsProcessed,
    //   "items"
    // );

    // Return total as decimal value (not cents)
    return itemsProcessed > 0 ? cartTotal : null;
  }

  /**
   * Apply custom prices from window.OjarPriceData to price elements
   * Called after page load and currency changes
   */
  /**
   * Apply custom prices from window.OjarPriceData to price elements (debounced)
   * Prevents excessive DOM updates from multiple rapid triggers
   */
  function applyMultiCurrencyPrices() {
    // Debounce: prevent calling the actual function too frequently
    clearTimeout(priceApplyTimeout);
    const now = Date.now();
    const timeSinceLastCall = now - lastPriceApplyTime;

    if (timeSinceLastCall >= MIN_PRICE_APPLY_INTERVAL) {
      // Enough time has passed, execute immediately
      applyMultiCurrencyPricesImpl();
      lastPriceApplyTime = now;
    } else {
      // Schedule for later to batch updates
      priceApplyTimeout = setTimeout(() => {
        applyMultiCurrencyPricesImpl();
        lastPriceApplyTime = Date.now();
      }, MIN_PRICE_APPLY_INTERVAL - timeSinceLastCall);
    }
  }

  /**
   * Internal implementation of price application
   */
  let _priceApplyRunning = false;
  function applyMultiCurrencyPricesImpl() {
    // Prevent re-entrant calls (e.g., from fetch interceptor during cart API call)
    if (_priceApplyRunning) return;
    _priceApplyRunning = true;
    try {
      _applyMultiCurrencyPricesCore();
    } finally {
      _priceApplyRunning = false;
    }
  }

  function _applyMultiCurrencyPricesCore() {
    // Exit if OjarPriceData is not available
    if (!window.OjarPriceData || typeof window.OjarPriceData !== "object") {
      // console.log(
      //   "[OjarCurrency] OjarPriceData not available, skipping price application"
      // );
      return;
    }

    // console.log(
    //   "[OjarCurrency] Applying custom prices for currency:",
    //   currentCurrency
    // );

    // Find all price containers marked for Ojar formatting (product pages, collections)
    const priceContainers = document.querySelectorAll(
      '[data-ojar-price-formatted="true"]'
    );

    priceContainers.forEach((container) => {
      const handle = container.dataset.productHandle;

      // Skip products with varying prices (e.g., 15ml vs 100ml variants)
      // Let Shopify's native variant pricing handle these
      if (container.dataset.priceVaries === "true") {
        // console.log("[OjarCurrency] Skipping price override for product with varying prices:", handle);
        return;
      }

      // Validate product handle and OjarPriceData
      if (!handle || !window.OjarPriceData[handle]) {
        // console.warn("[OjarCurrency] No price data for product:", handle);
        return;
      }

      // Get price from OjarPriceData for current currency
      const priceInCurrency = window.OjarPriceData[handle][currentCurrency];

      if (priceInCurrency === undefined) {
        // console.warn(
        //   "[OjarCurrency] No price for currency",
        //   currentCurrency,
        //   "in product:",
        //   handle
        // );
        return;
      }

      // Format the price
      const formattedPrice = formatPrice(priceInCurrency, currentCurrency);

      // Update all price display elements
      const priceItems = container.querySelectorAll(
        ".price-item--regular, .price-item--sale"
      );
      priceItems.forEach((item) => {
        // Check if price is inside a strikethrough (<s> tag) = compare-at price
        if (item.closest("s")) {
          // Convert the strikethrough (compare-at) price using conversion rate
          // Extract the original USD value from the rendered text
          const originalText = item.getAttribute("data-ojar-original-price") || item.textContent;
          // Store original USD text for future re-conversions
          if (!item.hasAttribute("data-ojar-original-price")) {
            item.setAttribute("data-ojar-original-price", item.textContent);
          }
          const numericMatch = originalText.match(/[\d,]+\.?\d*/);
          if (numericMatch) {
            const usdValue = parseFloat(numericMatch[0].replace(/,/g, ""));
            const conversionRate = getConversionRate(currentCurrency);
            const convertedPrice = usdValue * conversionRate;
            item.textContent = formatPrice(convertedPrice, currentCurrency);
          }
          return;
        }
        item.textContent = formattedPrice;
      });
    });

    // Also update cart line item prices if they exist
    const cartLineItems = document.querySelectorAll("[data-product-handle]");
    cartLineItems.forEach((item) => {
      const handle = item.dataset.productHandle;

      // Only process if it has product handle and we have price data
      if (!handle || !window.OjarPriceData[handle]) {
        return;
      }

      const priceInCurrency = window.OjarPriceData[handle][currentCurrency];

      if (priceInCurrency === undefined) {
        return;
      }

      const formattedPrice = formatPrice(priceInCurrency, currentCurrency);

      // Update cart item price if it's a price element
      if (item.classList.contains("oj-line-item__price")) {
        item.textContent = formattedPrice;
      }
    });

    // Update mobile cart item line total prices
    const cartMobileLineTotals = document.querySelectorAll(
      ".oj-cart-item-line-total-price"
    );
    cartMobileLineTotals.forEach((element) => {
      const cartRow = element.closest("tr[data-product-handle]");
      if (!cartRow) return;

      const handle = cartRow.dataset.productHandle;
      if (!handle || !window.OjarPriceData[handle]) {
        return;
      }

      const priceInCurrency = window.OjarPriceData[handle][currentCurrency];
      if (priceInCurrency === undefined) {
        return;
      }

      const quantityInput = cartRow.querySelector("[data-quantity-variant-id]");
      const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;

      // Calculate line total: price × quantity
      const lineTotal = priceInCurrency * quantity;
      const formattedLineTotal = formatPrice(lineTotal, currentCurrency);
      element.textContent = formattedLineTotal;
    });

    // Update cart total if it exists (update both cart page and drawer)
    const cartTotalValueElements = document.querySelectorAll(
      ".totals__total-value"
    );

    cartTotalValueElements.forEach((cartTotalValueElement) => {
      // STRATEGY: Always try line-item calculation first.
      // If that fails, use the AJAX cart API total (never re-read our own converted DOM text).
      const cartTotal = calculateCartTotalFromLineItems();
      if (cartTotal !== null) {
        const formattedTotal = formatPrice(cartTotal, currentCurrency);
        cartTotalValueElement.textContent = formattedTotal;
        cartTotalValueElement.setAttribute("data-ojar-converted", "true");
      } else {
        // Fallback: Use Shopify AJAX Cart API to get the real USD total
        // This avoids EVER reading from DOM text (which could be already converted)
        fetchCartTotalFromAPI(cartTotalValueElement);
      }
    });

    // console.log("[OjarCurrency] Price application complete");
  }

  /**
   * Initialize the currency system
   */

  /**
   * Fetch cart total from Shopify AJAX API (bulletproof fallback)
   * Never reads from DOM text to avoid re-conversion bugs
   */
  let _cartApiFetchInProgress = false;
  function fetchCartTotalFromAPI(element) {
    if (_cartApiFetchInProgress) return;
    _cartApiFetchInProgress = true;
    
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(r => r.json())
      .then(cart => {
        // cart.total_price is in cents (USD, store's base currency)
        const usdTotal = cart.total_price / 100;
        
        // Calculate converted total from individual items with OjarPriceData
        let convertedTotal = 0;
        let allItemsFound = true;
        
        cart.items.forEach(item => {
          const handle = item.handle;
          const quantity = item.quantity;
          const isSample = item.product_type === 'sample' || 
                          (item.tags && item.tags.includes('sample-product'));
          
          if (isSample || item.final_price === 0) {
            return; // Skip free/sample items
          }
          
          if (window.OjarPriceData && window.OjarPriceData[handle] && 
              window.OjarPriceData[handle][currentCurrency] !== undefined) {
            convertedTotal += window.OjarPriceData[handle][currentCurrency] * quantity;
          } else {
            // Product not in OjarPriceData - use conversion rate from USD
            const conversionRate = getConversionRate(currentCurrency);
            convertedTotal += (item.final_price / 100) * conversionRate * quantity;
            allItemsFound = false;
          }
        });
        
        const formattedTotal = formatPrice(convertedTotal, currentCurrency);
        element.textContent = formattedTotal;
        element.setAttribute("data-ojar-converted", "true");
      })
      .catch(err => {
        console.warn("[OjarCurrency] Cart API fetch failed:", err);
      })
      .finally(() => {
        _cartApiFetchInProgress = false;
      });
  }

  function init() {
    // console.log("[OjarCurrency] Initializing...");

    // Priority 1: Check for user-selected currency in localStorage
    const userSelected = localStorage.getItem("ojar_user_selected") === "true";
    const savedCurrency = localStorage.getItem(CONFIG.storageKey);

    if (
      userSelected &&
      savedCurrency &&
      SUPPORTED_CURRENCIES.includes(savedCurrency)
    ) {
      // console.log(
      //   "[OjarCurrency] Using user-selected currency:",
      //   savedCurrency
      // );
      currentCurrency = savedCurrency;
      currentCountry =
        localStorage.getItem(CONFIG.countryKey) || CONFIG.defaultCountry;

      // Ensure cookies are set (for server-side Liquid rendering)
      setCookie(CONFIG.cookieName, currentCurrency, CONFIG.cookieDays);
      setCookie(CONFIG.cookieCountryName, currentCountry, CONFIG.cookieDays);
    } else {
      // Priority 2: Check cookie (set by server from previous visit)
      const cookieCurrency = getCookie(CONFIG.cookieName);

      if (cookieCurrency && SUPPORTED_CURRENCIES.includes(cookieCurrency)) {
        // console.log("[OjarCurrency] Using cookie currency:", cookieCurrency);
        currentCurrency = cookieCurrency;
        currentCountry =
          getCookie(CONFIG.cookieCountryName) || CONFIG.defaultCountry;

        // Sync to localStorage
        localStorage.setItem(CONFIG.storageKey, currentCurrency);
        localStorage.setItem(CONFIG.countryKey, currentCountry);
      } else {
        // Priority 3: First visit - detect location
        // console.log("[OjarCurrency] First visit - detecting location...");
        detectLocation();
        return; // detectLocation handles init completion
      }
    }

    // Setup currency switcher UI
    setupCurrencySwitcher();

    // Set up instant conversion event listeners
    setupInstantConversionEvents();

    // Apply custom prices from OjarPriceData if available
    applyMultiCurrencyPrices();

    // Subscribe to cart update events to recalculate all prices and totals
    if (window.subscribe && window.PUB_SUB_EVENTS) {
      // Cart update event (item added/removed/quantity changed)
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => {
        // Clear all conversion markers so prices get recalculated fresh
        document.querySelectorAll("[data-ojar-converted]").forEach((el) => {
          el.removeAttribute("data-ojar-converted");
        });
        document.querySelectorAll("[data-ojar-original-usd-total]").forEach((el) => {
          el.removeAttribute("data-ojar-original-usd-total");
        });
        // Delay to ensure DOM is updated after cart changes
        setTimeout(() => {
          applyMultiCurrencyPrices();
        }, 300);
      });
    }
  }

  /**
   * Detect user location via IP and set currency
   */
  async function detectLocation() {
    // console.log("[OjarCurrency] Fetching geo location...");
    try {
      const response = await fetch(CONFIG.geoApiUrl);
      const data = await response.json();

      if (data.country_code) {
        currentCountry = data.country_code;
        currentCurrency =
          COUNTRY_CURRENCY_MAP[currentCountry] || CONFIG.defaultCurrency;

        // console.log(
        //   "[OjarCurrency] Detected country:",
        //   currentCountry,
        //   "-> Currency:",
        //   currentCurrency
        // );
      } else {
        // console.warn(
        //   "[OjarCurrency] No country_code in response, using default"
        // );
        currentCurrency = CONFIG.defaultCurrency;
        currentCountry = CONFIG.defaultCountry;
      }
    } catch (error) {
      // console.error("[OjarCurrency] Geo-detection failed:", error);
      currentCurrency = CONFIG.defaultCurrency;
      currentCountry = CONFIG.defaultCountry;
    }

    // Save to localStorage and cookies
    localStorage.setItem(CONFIG.storageKey, currentCurrency);
    localStorage.setItem(CONFIG.countryKey, currentCountry);
    setCookie(CONFIG.cookieName, currentCurrency, CONFIG.cookieDays);
    setCookie(CONFIG.cookieCountryName, currentCountry, CONFIG.cookieDays);

    // Refresh page to render with correct currency from server - BUT prevent in Editor to avoid loops
    if (window.Shopify && window.Shopify.designMode) {
      // console.log("[OjarCurrency] Design mode detected - preventing page reload");
    } else {
      // console.log(
      //   "[OjarCurrency] Currency detected, refreshing page for server-side rendering..."
      // );
      window.location.reload();
    }
  }

  /**
   * Set up currency switcher dropdown UI
   */
  function setupCurrencySwitcher() {
    const switchers = document.querySelectorAll(".ojar-currency-switcher");

    switchers.forEach((container) => {
      const btn = container.querySelector(".ojar-currency-btn");
      const list = container.querySelector(".ojar-currency-list");
      const options = container.querySelectorAll(".ojar-currency-option");

      if (!btn || !list) return;

      // Update button text to current currency
      const codeEl = container.querySelector("[data-current-currency]");
      if (codeEl) codeEl.textContent = currentCurrency;

      // Toggle dropdown on button click
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();

        const isExpanded = btn.getAttribute("aria-expanded") === "true";

        // Close all other switchers
        document.querySelectorAll(".ojar-currency-btn").forEach((b) => {
          if (b !== btn) {
            b.setAttribute("aria-expanded", "false");
          }
        });
        document.querySelectorAll(".ojar-currency-list").forEach((l) => {
          if (l !== list) {
            l.setAttribute("aria-hidden", "true");
            l.hidden = true;
          }
        });

        // Toggle current dropdown
        if (!isExpanded) {
          btn.setAttribute("aria-expanded", "true");
          list.setAttribute("aria-hidden", "false");
          list.hidden = false;
        } else {
          btn.setAttribute("aria-expanded", "false");
          list.setAttribute("aria-hidden", "true");
          list.hidden = true;
        }
      });

      // Handle currency option click
      options.forEach((opt) => {
        opt.addEventListener("click", (e) => {
          e.stopPropagation();

          const code = opt.dataset.currencyCode;
          if (code && SUPPORTED_CURRENCIES.includes(code)) {
            setCurrency(code);
          }
        });
      });
    });

    // Close dropdowns when clicking outside
    document.addEventListener("click", (e) => {
      if (!e.target.closest(".ojar-currency-switcher")) {
        document.querySelectorAll(".ojar-currency-btn").forEach((b) => {
          b.setAttribute("aria-expanded", "false");
        });
        document.querySelectorAll(".ojar-currency-list").forEach((l) => {
          l.setAttribute("aria-hidden", "true");
          l.hidden = true;
        });
      }
    });

    // Handle native select elements (mobile/footer)
    document
      .querySelectorAll("[data-ojar-currency-select]")
      .forEach((select) => {
        select.value = currentCurrency;

        select.addEventListener("change", (e) => {
          const code = e.target.value;
          if (code && SUPPORTED_CURRENCIES.includes(code)) {
            setCurrency(code);
          }
        });
      });
  }

  /**
   * Setup instant currency conversion event handlers
   * These handlers ensure prices convert immediately when events occur
   */
  function setupInstantConversionEvents() {
    // Mark as already setup to prevent duplicate initialization
    if (window.OjarCurrencyEventsSetup === true) {
      // console.log(
      //   "[OjarCurrency] Event handlers already initialized, skipping"
      // );
      return;
    }
    window.OjarCurrencyEventsSetup = true;

    // 1. Quantity changes on cart page
    document.addEventListener(
      "change",
      (e) => {
        if (
          e.target.matches('[data-quantity-input], input[name*="quantity"]')
        ) {
          setTimeout(() => applyMultiCurrencyPrices(), 0);
        }
      },
      true
    );

    // 2. Cart drawer open/close events
    // Note: Removed MutationObserver as it caused scroll lag by firing on every DOM change
    // Instead, we rely on the fetch interception and Shopify's cartUpdate pub/sub events
    document.addEventListener(
      "click",
      (e) => {
        // Detect cart drawer toggle button clicks
        if (
          e.target.closest(
            '[data-cart-drawer-trigger], [href*="/cart"], .cart-icon-bubble, #cart-icon-bubble'
          )
        ) {
          // Apply prices after drawer opens
          setTimeout(() => applyMultiCurrencyPrices(), 150);
        }
      },
      true
    );

    // 3. Variant selection
    document.addEventListener(
      "change",
      (e) => {
        if (e.target.matches('[data-single-variant-selector], [name="id"]')) {
          setTimeout(() => applyMultiCurrencyPrices(), 100);
        }
      },
      true
    );

    // 4. Form submissions (add to cart, etc.)
    document.addEventListener(
      "submit",
      (e) => {
        if (e.target.matches('[action*="/cart"], form[data-add-to-cart]')) {
          setTimeout(() => applyMultiCurrencyPrices(), 200);
        }
      },
      true
    );

    // 5. Intercept fetch calls for cart operations (only wrap once)
    if (!window.OjarCurrencyFetchWrapped) {
      window.OjarCurrencyFetchWrapped = true;
      const originalFetch = window.fetch;
      window.fetch = function (...args) {
        const result = originalFetch.apply(this, args);
        // Only trigger price recalculation for cart-modifying requests, NOT for /cart.js reads
        const url = typeof args[0] === 'string' ? args[0] : (args[0]?.url || '');
        const isCartRead = url === '/cart.js' || url === '/cart.json';
        if (!isCartRead) {
          result
            .then(() => {
              setTimeout(() => applyMultiCurrencyPrices(), 200);
            })
            .catch(() => { });
        }
        return result;
      };
    }
  }

  /**
   * Set currency (user selection)
   */
  function setCurrency(currencyCode) {
    if (!SUPPORTED_CURRENCIES.includes(currencyCode)) return;

    // console.log("[OjarCurrency] User selecting currency:", currencyCode);
    currentCurrency = currencyCode;

    // Save to localStorage
    localStorage.setItem(CONFIG.storageKey, currencyCode);
    localStorage.setItem("ojar_user_selected", "true");

    // Set cookies for server-side rendering
    setCookie(CONFIG.cookieName, currencyCode, CONFIG.cookieDays);
    setCookie(CONFIG.cookieCountryName, currentCountry, CONFIG.cookieDays);

    // Clear ALL stored original values and conversion markers before re-applying
    document.querySelectorAll("[data-ojar-original-price]").forEach((el) => {
      // Keep the original USD value - just reconvert from it
    });
    document.querySelectorAll("[data-ojar-converted]").forEach((el) => {
      el.removeAttribute("data-ojar-converted");
    });
    // Update prices immediately with new currency
    applyMultiCurrencyPrices();

    // Update currency switcher UI to reflect selected currency
    const currencySwitchers = document.querySelectorAll(
      "[data-current-currency]"
    );
    currencySwitchers.forEach((el) => {
      el.textContent = currencyCode;
    });

    // Update native select elements
    document
      .querySelectorAll("[data-ojar-currency-select]")
      .forEach((select) => {
        select.value = currencyCode;
      });
  }

  /**
   * Public API
   */
  window.OjarCurrency = {
    init,
    setCurrency,
    getCurrentCurrency: () => currentCurrency,
    getCurrencyMetafieldKey: () =>
      currentCurrency === "USD"
        ? ""
        : `product_price_in_${currentCurrency.toLowerCase()}`,
    getCurrentCountry: () => currentCountry,
    getSupportedCurrencies: () => SUPPORTED_CURRENCIES,
    applyMultiCurrencyPrices, // Expose for manual calls if needed
  };

  // Initialize on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
